/**
 * @file planYearService.js
 * @description Service layer for handling operations related to Plan Years.
 */
import { clientApiHttp, systemHttp } from './httpService';

/**
 * Retrieve the tenant ID from local storage.
 * @returns {number} The tenant ID
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');

/**
 * Searches for plan years with pagination and filtering options.
 *
 * @param {number} tenantId - The ID of the tenant
 * @param {number} page - The page number for pagination
 * @param {number} pageSize - The number of items per page
 * @param {string} term - Search term for filtering plan years by name or code
 * @param {string} sort - Sort field (e.g., "name", "code", "startDate", "endDate", "isOffCycle")
 * @param {string} order - Sort order ("asc" or "desc")
 * @returns {Promise<Object>} A promise that resolves to the paginated plan year list
 */
export const searchPlanYears = async (
    tenantId = null,
    page = 1,
    pageSize = 20,
    term = '',
    sort = 'name',
    order = 'asc',
    filterByPopulationGroup = false,
) => {
    try {
        if (!tenantId) tenantId = getTenantId();
        const params = new URLSearchParams({
            page,
            pageSize,
            sort,
            order,
        });

        if (term) params.append('term', term);
        if (filterByPopulationGroup) {
            params.append('filterByPopulationGroup', 'true');
        }

        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan-year?${params.toString()}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error searching plan years:', error);
        throw error;
    }
};

/**
 * Fetches a plan year by ID.
 *
 * @param {number} planYearId - The ID of the plan year
 * @returns {Promise<Object>} A promise that resolves to the plan year data
 */
export const fetchPlanYearById = async (
    planYearId,
    filterByPopulationGroup = false,
) => {
    try {
        const tenantId = getTenantId();
        const query = filterByPopulationGroup
            ? '?filterByPopulationGroup=true'
            : '';
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan-year/${planYearId}${query}`,
        );
        return response.data;
    } catch (error) {
        console.error(`Error fetching plan year with ID ${planYearId}:`, error);
        throw error;
    }
};

/**
 * Creates a new plan year.
 *
 * @param {Object} planYearData - The data for the new plan year
 * @returns {Promise<Object>} A promise that resolves to the created plan year data
 */
export const createPlanYear = async planYearData => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/plan-year`,
            { ...planYearData, tenantId },
        );
        return response.data;
    } catch (error) {
        console.error('Error creating plan year:', error);
        throw error;
    }
};

/**
 * Updates an existing plan year.
 *
 * @param {Object} planYearData - The updated data for the plan year
 * @returns {Promise<Object>} A promise that resolves to the updated plan year data
 */
export const updatePlanYear = async planYearData => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/plan-year`,
            planYearData,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating plan year:', error);
        throw error;
    }
};

/**
 * Deletes a plan year by ID.
 *
 * @param {number} planYearId - The ID of the plan year to delete
 * @returns {Promise<Object>} A promise that resolves to the deletion status
 */
export const deletePlanYear = async planYearId => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/plan-year/${planYearId}`,
        );
        return response.data;
    } catch (error) {
        console.error(`Error deleting plan year with ID ${planYearId}:`, error);
        throw error;
    }
};

/**
 * Attaches an asset to a plan year.
 * This is a helper method that updates the plan year by adding an asset to its planYearAssets array.
 *
 * @param {number} planYearId - The ID of the plan year
 * @param {number} assetId - The ID of the asset to attach
 * @returns {Promise<Object>} A promise that resolves to the updated plan year data
 */
export const attachAssetToPlanYear = async (planYearId, assetId) => {
    try {
        const planYear = await fetchPlanYearById(planYearId);

        if (!planYear.planYearAssets) {
            planYear.planYearAssets = [];
        }

        const assetAlreadyAttached = planYear.planYearAssets.some(
            asset => asset.assetId === assetId,
        );

        if (!assetAlreadyAttached) {
            planYear.planYearAssets.push({
                id: 0,
                planYearId: planYearId,
                assetId: assetId,
            });
        }

        return await updatePlanYear(planYear);
    } catch (error) {
        console.error(
            `Error attaching asset ${assetId} to plan year ${planYearId}:`,
            error,
        );
        throw error;
    }
};

/**
 * Removes an asset from a plan year.
 * This is a helper method that updates the plan year by removing an asset from its planYearAssets array.
 *
 * @param {number} planYearId - The ID of the plan year
 * @param {number} assetId - The ID of the asset to remove
 * @returns {Promise<Object>} A promise that resolves to the updated plan year data
 */
export const removeAssetFromPlanYear = async (planYearId, assetId) => {
    try {
        const planYear = await fetchPlanYearById(planYearId);

        if (planYear.planYearAssets) {
            planYear.planYearAssets = planYear.planYearAssets.filter(
                asset => asset.assetId !== assetId,
            );
        }

        return await updatePlanYear(planYear);
    } catch (error) {
        console.error(
            `Error removing asset ${assetId} from plan year ${planYearId}:`,
            error,
        );
        throw error;
    }
};
